import { Button, Flex, message, TablePaginationConfig } from "antd";
import CreateModal from "./components/CreateModal";
import EditModal from "./components/EditModal";
import { getColumns } from "./utils/table";
import { ExpenseDescription } from "@/types/expenseDescription";
import { DEFAULT_PAGINATION } from "@/constants/table";
import { useMemo, useState } from "react";
import useModal from "@/hooks/useModal";
import {
  useGetExpenseDescriptions,
  useGetExpenseDescriptionsOptions,
} from "../../hooks/useExpenseDescriptions";
import {
  useDeleteExpenseDescription,
  useRestoreExpenseDescription,
  useUpdateExpenseDescription,
} from "../../hooks/useExpenseDescriptions";
import { SortCondition } from "@/components/table/hooks/useSort";
import { FilterCondition } from "@/components/table/hooks/useFilter";
import Table, { FilterOptions } from "@/components/table";

const ExpenseDescriptions = () => {
  const [sort, setSort] = useState<SortCondition | undefined>();
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [pagination, setPagination] =
    useState<TablePaginationConfig>(DEFAULT_PAGINATION);

  const {
    isModalOpen: isAddModalOpen,
    openModal: openAddModal,
    closeModal: closeAddModal,
  } = useModal();
  const {
    modalData: editModalData,
    isModalOpen: isEditModalOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
  } = useModal<ExpenseDescription>();

  const { mutate: deleteExpenseDescription } = useDeleteExpenseDescription();
  const { mutate: restoreExpenseDescription } = useRestoreExpenseDescription();
  const { mutate: updateExpenseDescription } = useUpdateExpenseDescription();

  const {
    data: expenseDescriptions,
    isFetching,
    refetch,
  } = useGetExpenseDescriptions({
    params: {
      include_inactive: true,
      sort: sort ? [{ field: sort.field, order: sort.order }] : undefined,
      filters: filters.length > 0 ? filters : undefined,
      limit: pagination?.pageSize,
      page: pagination?.current,
      offset: ((pagination?.current || 1) - 1) * (pagination?.pageSize || 10),
    },
  });

  // Get filter options
  const { data: options } = useGetExpenseDescriptionsOptions({
    params: {
      only: "name,expense_type.id,expense_type.name,unit.id,unit.name",
      limit: -1,
    },
  });

  const filterOptions = useMemo(
    () => ({
      name: (options || []).reduce((acc, item) => {
        if (item.name && !acc.some((option) => option.value === item?.name)) {
          acc.push({
            text: item.name,
            value: item.name,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      expense_type_id: (options || []).reduce((acc, item) => {
        if (
          item.expense_type &&
          !acc.some((option) => option.value === item?.expense_type?.id)
        ) {
          acc.push({
            text: item.expense_type.name,
            value: item.expense_type.id,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      unit_id: (options || []).reduce((acc, item) => {
        if (
          item.unit &&
          !acc.some((option) => option.value === item?.unit?.id)
        ) {
          acc.push({
            text: item.unit.name,
            value: item.unit.id,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
    }),
    [options]
  );

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      if (isActive) {
        restoreExpenseDescription(
          { id },
          {
            onSuccess: () => {
              message.success("Successfully activated expense description");
            },
          }
        );
      } else {
        deleteExpenseDescription(
          { id },
          {
            onSuccess: () => {
              message.success("Successfully deactivated expense description");
            },
          }
        );
      }
    } catch (error) {
      throw error;
    }
  };

  const handleToggleReview = async (id: string, isReviewed: boolean) => {
    try {
      // Find the current record to get required fields
      const currentRecord = expenseDescriptions?.data?.find(
        (desc) => desc.id === id
      );
      if (!currentRecord) {
        message.error("Record not found");
        return;
      }

      updateExpenseDescription(
        {
          id,
          name: currentRecord.name,
          expense_type_id: currentRecord.expense_type?.id || "",
          unit_id: currentRecord.unit?.id || null,
          is_reviewed: isReviewed,
        },
        {
          onSuccess: () => {
            message.success(
              isReviewed
                ? "Successfully marked expense description for review"
                : "Successfully removed expense description from review"
            );
          },
        }
      );
    } catch (error) {
      throw error;
    }
  };

  return (
    <Flex vertical gap={16}>
      <Flex justify="flex-end" align="center">
        <Button type="primary" onClick={openAddModal}>
          New Description
        </Button>
      </Flex>

      <Table
        columns={(props) =>
          getColumns({
            ...props,
            onEdit: openEditModal,
            onToggleActive: handleToggleActive,
            onToggleReview: handleToggleReview,
            filterOptions,
          })
        }
        dataSource={expenseDescriptions?.data || []}
        loading={isFetching}
        pagination={{
          defaultPagination: {
            ...DEFAULT_PAGINATION,
            showSizeChanger: true,
          },
          dataPagination: expenseDescriptions?.pagination,
          onChange: setPagination,
        }}
        scroll={{
          x: "max-content",
        }}
        initialSort={sort}
        onSort={setSort}
        onFilter={setFilters}
      />

      <CreateModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onSuccess={() => refetch()}
      />
      <EditModal
        data={editModalData}
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onSuccess={() => refetch()}
      />
    </Flex>
  );
};

export default ExpenseDescriptions;
